/**
 * Model API calls.
 *
 * Mirrors the legacy AngularJS $modelRepository and model-related REST endpoints.
 * Models are the central entity in Brewlytics - they represent analytic workflows
 * built on the canvas.
 */
import apiClient from './client';
import { buildAuditSearchUrl, type AuditSearchParams } from './auditSearch';
import type { Model, SearchParams, AclEntry, SkipLimitSearchResponse } from '@/types';
import { ACL_SAVE_HEADERS, type AclActor } from './acl';

// ── Model audits ──────────────────────────────────────────────────────

export type ModelAuditType =
  | 'SEARCH'
  | 'EXECUTE'
  | 'CREATE'
  | 'READ'
  | 'UPDATE'
  | 'DELETE';

/** Common attributes on every model audit row, plus the verb-specific extras
 *  (SEARCH has `text`/`count`/`limit`/`skip`; EXECUTE adds job/task/state/etc.). */
export interface ModelAuditAttributes {
  dn?: string;
  // SEARCH
  text?: string;
  count?: number;
  limit?: number;
  skip?: number;
  // EXECUTE
  actor?: string;
  jobId?: string;
  taskId?: string;
  name?: string;
  namespace?: string;
  state?: string;
  durationMS?: string;
  [k: string]: unknown;
}

export interface ModelAuditEntry {
  subject?: string;
  verb?: ModelAuditType;
  attributes?: ModelAuditAttributes;
  timestamp?: string;
  /** Either a specific model URN (`urn:brew:models:<uuid>`) or the collection
   *  root `urn:brew:models` for SEARCH events. */
  object?: string;
}

/**
 * Search the model audit log.
 *
 *   GET api/models/audits/search?^auditType=EXECUTE&text=*&limit=20&skip=0&sort=-timestamp
 *
 * The `^auditType` predicate prefix must stay unencoded so we build the URL
 * by hand (same pattern as searchProfileAudits / searchTaskAudits). Omit
 * `auditType` to fetch every verb at once.
 */
export async function searchModelAudits(
  params: AuditSearchParams<ModelAuditType> = {},
): Promise<SkipLimitSearchResponse<ModelAuditEntry>> {
  const url = buildAuditSearchUrl('api/models/audits/search', params);
  const res = await apiClient.get<SkipLimitSearchResponse<ModelAuditEntry>>(url);
  return res.data;
}

/** List models with optional filters. API returns a flat array, not paginated. */
export async function listModels(params?: SearchParams & { published?: boolean }): Promise<Model[]> {
  const res = await apiClient.get<Model[]>('api/models', { params });
  return res.data;
}

/**
 * Why a model is loaded, when it isn't to open it. `preview`: a Discover card
 * preview. The server keeps the READ audit row and marks it `via: preview`, so
 * previews are told apart from opens (jorigin/brewlytics#2208).
 */
export interface GetModelOptions {
  via?: 'preview';
}

/** Get a specific model by ID. */
export async function getModel(id: string, options: GetModelOptions = {}): Promise<Model> {
  const res = await apiClient.get<Model>(
    `api/models/${id}`,
    options.via ? { params: { via: options.via } } : undefined,
  );
  return res.data;
}

/**
 * Toggle the current user's "follow" (favorite) state on a model.
 *
 *   POST api/models/:id { action: 'follow', options: { toggle: true } }
 *
 * Server returns the new state as a bare boolean (`true` = followed/
 * favorited, `false` = not). Initial state is exposed on the model's
 * `$following` field returned by `getModel`.
 */
export async function toggleModelFavorite(id: string): Promise<boolean> {
  const res = await apiClient.post<boolean>(`api/models/${id}`, {
    action: 'follow',
    options: { toggle: true },
  });
  return !!res.data;
}

/** Create a new model. */
export async function createModel(data: Partial<Model>): Promise<Model> {
  const res = await apiClient.post<Model>('api/models', data);
  return res.data;
}

/** Update a model. Payload must include _id. */
export async function updateModel(data: Partial<Model>): Promise<Model> {
  const res = await apiClient.put<Model>('api/models', data);
  return res.data;
}

/**
 * Save (create or update) a model's parameter set.
 * Used by the draft-save flow before the model PUT to ensure the parameter
 * set exists server-side — mirrors legacy parameterSets.save().
 */
export async function updateModelParameterSet(
  modelId: string,
  parameterSet: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await apiClient.put(`api/models/${modelId}/parameters`, parameterSet);
  return res.data as Record<string, unknown>;
}

/** Delete a model. */
export async function deleteModel(id: string): Promise<void> {
  await apiClient.delete(`api/models/${id}`);
}

/** Relative URL of a model's avatar image. Append `?v=...` for cache-busting. */
export function getModelAvatarUrl(id: string): string {
  return `api/models/${id}/avatar`;
}

/**
 * Upload (or replace) a model's avatar image. Server accepts JPEG/PNG.
 * Returns the new Document's UUID (the same value the server stores under
 * the model's `avatar` field).
 */
export async function uploadModelAvatar(id: string, file: File | Blob, filename = 'avatar.png'): Promise<string> {
  const formData = new FormData();
  // FormData needs a filename for Blob → use the provided one (server validates extension).
  formData.append('file', file instanceof File ? file : new File([file], filename, { type: file.type }));
  const res = await apiClient.post(`api/models/${id}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const data = res.data as { _id?: string } | undefined;
  return data?._id ?? '';
}

/** Remove a model's avatar. */
export async function deleteModelAvatar(id: string): Promise<void> {
  await apiClient.delete(`api/models/${id}/avatar`);
}

/** Relative URL of a model's canvas watermark image. Append `?v=...` for cache-busting. */
export function getModelWatermarkUrl(id: string): string {
  return `api/models/${id}/watermark`;
}

/**
 * Upload (or replace) a model's canvas watermark image. Server accepts JPEG/PNG and
 * grants the resulting document EVERYONE-read so all model viewers can fetch it.
 * Returns the new Document's UUID (the same value the server stores under
 * the model's `watermark` field).
 */
export async function uploadModelWatermark(id: string, file: File | Blob, filename = 'watermark.png'): Promise<string> {
  const formData = new FormData();
  formData.append('file', file instanceof File ? file : new File([file], filename, { type: file.type }));
  const res = await apiClient.post(`api/models/${id}/watermark`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const data = res.data as { _id?: string } | undefined;
  return data?._id ?? '';
}

/** Remove a model's watermark. */
export async function deleteModelWatermark(id: string): Promise<void> {
  await apiClient.delete(`api/models/${id}/watermark`);
}

/** Get model count. */
export async function getModelCount(): Promise<number> {
  const res = await apiClient.get<number | string>('api/models/count');
  return Number(res.data) || 0;
}

/** Search models. API returns { total, data: Model[], limit, skip }. */
export interface SearchResult {
  data: Model[];
  total: number;
}

export async function searchModels(
  text: string,
  params?: { brief?: boolean; limit?: number; skip?: number; ns?: string; sort?: string },
): Promise<SearchResult> {
  const res = await apiClient.get('api/models/search', { params: { text, ...params } });
  const body = res.data;
  if (Array.isArray(body)) return { data: body, total: body.length };
  return { data: body.data || [], total: body.total ?? (body.data?.length || 0) };
}

/** Get editor's pick models (tagged with systag 'editorspick'). */
export async function getEditorsPicks(): Promise<Model[]> {
  const res = await apiClient.get('api/models/search', {
    params: { '^metadata.systags': 'editorspick' },
  });
  const body = res.data;
  if (Array.isArray(body)) return body;
  return body.data || [];
}

/** Get model tags. */
export async function getModelTags(): Promise<string[]> {
  const res = await apiClient.get<string[]>('api/models/tags');
  return res.data;
}

/**
 * Autocomplete suggestions for model tags.
 *
 * Calls `GET /api/models/tags?query=…`. The legacy server may answer with
 * either a plain array of tag names or a `{ tag: count }` map; both shapes
 * are normalized here to `{ tag, count }[]` ordered by descending count.
 */
export interface ModelTagSuggestion {
  tag: string;
  count: number;
}

export async function searchModelTags(query: string): Promise<ModelTagSuggestion[]> {
  const res = await apiClient.get<string[] | Record<string, number>>('api/models/tags', {
    params: { query },
  });
  const data = res.data;
  if (Array.isArray(data)) return data.map((tag) => ({ tag, count: 0 }));
  return Object.entries(data || {})
    .map(([tag, count]) => ({ tag, count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count);
}

/** Get system tags. */
export async function getSystemTags(): Promise<string[]> {
  const res = await apiClient.get<string[]>('api/models/systags');
  return res.data;
}

/** Get trending models. */
export async function getTrendingModels(params?: { limit?: number }): Promise<Model[]> {
  const res = await apiClient.get<Model[]>('api/models/trending', { params });
  return res.data;
}

/** Get model ACL / sharing info. Returns full ACL object with $actors array. */
export async function getModelAcl(id: string): Promise<ModelAclResponse> {
  const res = await apiClient.get<ModelAclResponse>(`api/models/${id}/acl`);
  return res.data;
}

/**
 * Get model sharing info: owners, collaborators and guests. A profile is a
 * hydrated object; a group is its ref alone (see `SharingParticipant`), and
 * its label is brewauth's (`ParticipantCards`).
 */
export async function getModelSharing(id: string): Promise<ModelSharingResponse> {
  const res = await apiClient.get<ModelSharingResponse>(`api/models/${id}/sharing`);
  return res.data;
}

/** Single participant in a model's sharing payload. */
export interface SharingParticipant {
  _id: string;
  $reference?: string;
  /**
   * `individual` for users, `group` for groups. Java names a group by its ref
   * alone (jorigin/brewauth#137): `_id`, `$reference`, `$type` and `$icon`, with
   * no `metadata`. Its label, whether it is a filter group, and its filter come
   * from brewauth (`ParticipantCards`), never from this response. Typed as
   * any string so an unknown kind still loads.
   */
  $type?: string;
  $role?: string;
  $icon?: string;
  $isSystem?: boolean;
  metadata?: {
    label?: { lang?: string; value: string; capco?: string } | string;
    desc?: { lang?: string; value: string; capco?: string };
    /** Profile DNs are stored as full strings; CN is parsed at display time. */
    createdBy?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ModelSharingResponse {
  _id: string;
  $self?: string;
  $reference?: string;
  $type?: string;
  $role?: string;
  $owner?: SharingParticipant | string;
  $owners?: SharingParticipant[];
  $collaborators?: SharingParticipant[];
  $guests?: SharingParticipant[];
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ModelAclActor {
  _id: string;
  $type: 'individual' | 'group';
  $role: string;
  $icon?: string;
  $permissions?: string[];
  metadata?: {
    label?: { lang?: string; value: string; capco?: string };
    desc?: { lang?: string; value: string; capco?: string };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ModelAclResponse {
  _id: string;
  $self: string;
  $owner: string;
  $role: string;
  $actors: ModelAclActor[];
  $groups?: string[];
  $reference?: string;
  $type?: string;
  [key: string]: unknown;
}

/** Update model ACL. */
export async function updateModelAcl(id: string, acl: AclEntry[]): Promise<void> {
  await apiClient.put(`api/models/${id}/acl`, acl, { headers: ACL_SAVE_HEADERS });
}

/** Get model definition (for Job execution). */
export async function getModelDefinition(id: string): Promise<unknown> {
  const res = await apiClient.get(`api/persistence/models/${id}/definition`);
  return res.data;
}

/** Get model audit concordance data. */
export async function getModelAudits(): Promise<unknown> {
  const res = await apiClient.get('api/models/audits/concordance');
  return res.data;
}

/** One bucket from `api/models/audits/histogram`. */
export interface ModelsAuditHistogramBucket {
  ts: number;
  at?: string;
  total?: number;
  read?: number;
  create?: number;
  update?: number;
  delete?: number;
  grant?: number;
  execute?: number;
  search?: number;
}

/** Response from `api/models/audits/histogram`. */
export interface ModelsAuditHistogramResponse {
  duration: string;
  values: ModelsAuditHistogramBucket[];
  epoch: string;
  units: string;
  type: string;
}

/** Get the per-bucket audit histogram across all models. */
export async function getModelsAuditHistogram(
  params: { days?: number; samples?: number } = {},
): Promise<ModelsAuditHistogramResponse> {
  const { days = 7, samples = 56 } = params;
  const res = await apiClient.get<ModelsAuditHistogramResponse>(
    'api/models/audits/histogram',
    { params: { days, samples } },
  );
  return res.data;
}

/**
 * List the profiles "following" (favoriting) a model.
 *
 *   GET api/models/:id/followers
 *
 * The response carries the same shape as the actors returned by ACL APIs,
 * so it's typed as `AclActor[]` for reuse with `ActorAvatar`, etc.
 */
export async function getModelFollowers(modelId: string): Promise<AclActor[]> {
  const res = await apiClient.get<AclActor[]>(
    `api/models/${encodeURIComponent(modelId)}/followers`,
  );
  return res.data ?? [];
}

/**
 * List the models that reference this model as a sub-component.
 *
 *   GET api/models/:id/usages
 *
 * Returns the parent models in `Model` shape (with `metadata.label`, owner
 * fields, etc.) — used by the HelpModelDetailPage's "Sub-Model Usage" card.
 */
export async function getModelUsages(modelId: string): Promise<Model[]> {
  const res = await apiClient.get<Model[]>(
    `api/models/${encodeURIComponent(modelId)}/usages`,
  );
  return res.data ?? [];
}

/**
 * List the profiles that have executed this model.
 *
 *   GET api/models/:id/users
 *
 * Response shape matches `AclActor` (label/avatar/$reference), so we reuse
 * that type and the shared `ActorAvatar` for rendering.
 */
export async function getModelUsers(modelId: string): Promise<AclActor[]> {
  const res = await apiClient.get<AclActor[]>(
    `api/models/${encodeURIComponent(modelId)}/users`,
  );
  return res.data ?? [];
}

/**
 * Search EXECUTE audit rows for a single model (most-recent first).
 *
 *   GET api/models/:id/audits/search?^auditType=EXECUTE&limit=10&skip=0&sort=-timestamp
 *
 * The legacy `^` predicate prefix must stay unencoded so we assemble the URL
 * by hand. Returns the standard `{ total, data, limit, skip }` envelope used
 * by every audit search endpoint.
 */
export async function searchModelExecuteAudits(
  modelId: string,
  params: { limit?: number; skip?: number } = {},
): Promise<SkipLimitSearchResponse<ModelAuditEntry>> {
  const { limit = 10, skip = 0 } = params;
  const url =
    `api/models/${encodeURIComponent(modelId)}/audits/search` +
    `?^auditType=EXECUTE` +
    `&limit=${limit}` +
    `&skip=${skip}` +
    `&sort=-timestamp`;
  const res = await apiClient.get<SkipLimitSearchResponse<ModelAuditEntry>>(url);
  return res.data;
}

/** Get the per-bucket audit histogram scoped to a single model. */
export async function getModelAuditHistogram(
  modelId: string,
  params: { days?: number; samples?: number } = {},
): Promise<ModelsAuditHistogramResponse> {
  const { days = 15, samples = 15 } = params;
  const res = await apiClient.get<ModelsAuditHistogramResponse>(
    `api/models/${encodeURIComponent(modelId)}/audits/histogram`,
    { params: { days, samples } },
  );
  return res.data;
}

/** Get published models. API returns a flat array, not paginated. */
export async function getPublishedModels(params?: SearchParams): Promise<Model[]> {
  const res = await apiClient.get<Model[]>('api/models', {
    params: { ...params, published: true },
  });
  return res.data;
}
